export const guestRepository = 'docker-dev.yml.world/any-fantasy/anyfantasy/openclaw-personal-guest';
export type RuntimeRelease = {
  id: string; app_id: string; stage_id: string; executor_id: string; commit_sha: string;
  containers: Array<{ service: string; container_name: string; container_id: string; image_id: string }>;
};
export type PersonalAgentPlan = {
  admission_id: string; app_id: string; reviewed_source_sha: string;
  release: { app_id: string; stage_id: string; release_id: string; executor_id: string;
    commit_sha: string; container_id: string; image_id: string };
  vm: { guest: { reference: string; config_digest: string };
    resources: { memory_mib: number; vcpu_count: number; disk_bytes: number } };
  ttl_seconds: number; max_requests: number; max_tokens: number; max_cost: number; enabled: boolean;
};
export type SetupStatus = {
  plan: PersonalAgentPlan | null; legacy_configured: boolean; ready: boolean; readiness_error: string | null;
};
export type RuntimeDraft = {
  image: string; configDigest: string; memory: number; cpus: number; disk: number;
  minutes: number; requests: number; tokens: number; cost: number;
};
export const initialDraft: RuntimeDraft = {
  image: '', configDigest: '', memory: 4096, cpus: 2, disk: 16,
  minutes: 30, requests: 8, tokens: 16384, cost: 1,
};

export function chooseContainer(release: RuntimeRelease | null, preferred = ''): string {
  const candidates = release?.containers ?? [];
  if (candidates.some(item => item.container_id === preferred)) return preferred;
  if (candidates.length === 1) return candidates[0].container_id;
  const backend = candidates.filter(item => item.service === 'app');
  return backend.length === 1 ? backend[0].container_id : '';
}

export function planDraft(plan: PersonalAgentPlan): RuntimeDraft {
  return { image: plan.vm.guest.reference, configDigest: plan.vm.guest.config_digest,
    memory: plan.vm.resources.memory_mib, cpus: plan.vm.resources.vcpu_count,
    disk: plan.vm.resources.disk_bytes / 1024 ** 3, minutes: plan.ttl_seconds / 60,
    requests: plan.max_requests, tokens: plan.max_tokens, cost: plan.max_cost };
}

export function createPlan(draft: RuntimeDraft, release: RuntimeRelease, containerId: string, admissionId: string): PersonalAgentPlan {
  const container = release.containers.find(item => item.container_id === containerId);
  const sha = /^sha256:[a-f0-9]{64}$/;
  if (!container || !sha.test(container.image_id) || !/^[a-f0-9]{64}$/.test(container.container_id)
    || !/^[a-f0-9]{40}$/.test(release.commit_sha)) throw new Error('Choose a container from the current verified deployment.');
  if (!draft.image.startsWith(`${guestRepository}@`) || !sha.test(draft.image.slice(guestRepository.length + 1))
    || !sha.test(draft.configDigest)) throw new Error('Enter the immutable guest image and config digests from its publication receipt.');
  const bounded = (n: number, min: number, max: number) => Number.isSafeInteger(n) && n >= min && n <= max;
  if (!bounded(draft.memory, 2048, 8192) || !bounded(draft.cpus, 1, 4) || !bounded(draft.disk, 8, 64)
    || !Number.isFinite(draft.minutes) || draft.minutes < 1 || draft.minutes > 60 || !Number.isInteger(draft.minutes * 60)
    || !bounded(draft.requests, 1, Number.MAX_SAFE_INTEGER) || !bounded(draft.tokens, 1, Number.MAX_SAFE_INTEGER)
    || !Number.isFinite(draft.cost) || draft.cost <= 0) throw new Error('Enter valid VM limits and a finite model allowance of up to one hour.');
  return { admission_id: admissionId, app_id: release.app_id, reviewed_source_sha: release.commit_sha,
    release: { app_id: release.app_id, stage_id: release.stage_id, release_id: release.id,
      executor_id: release.executor_id, commit_sha: release.commit_sha, container_id: container.container_id, image_id: container.image_id },
    vm: { guest: { reference: draft.image, config_digest: draft.configDigest },
      resources: { memory_mib: draft.memory, vcpu_count: draft.cpus, disk_bytes: draft.disk * 1024 ** 3 } },
    ttl_seconds: draft.minutes * 60, max_requests: draft.requests, max_tokens: draft.tokens, max_cost: draft.cost, enabled: true };
}

export function setupMessage(code: string | null): string {
  const messages: Record<string, string> = {
    personal_provisioning_plan_unavailable: 'Assistant setup has not been approved yet.',
    personal_provisioning_disabled: 'New assistant starts are disabled.',
    personal_provisioning_release_mismatch: 'The approved backend deployment has changed. The saved approval needs operator review.',
    personal_provisioning_executor_unavailable: 'The executor must be connected with healthy VM storage before setup can be approved.',
    personal_vm_registry_unavailable: 'Configure the environment’s private registry access before approving setup.',
    personal_provisioning_private_broker_required: 'Configure the private model connection before approving setup.',
    personal_vm_runtime_required: 'A VM runtime needs approval before new assistants can start.',
  };
  return code && messages[code] ? messages[code] : 'Server setup needs attention before assistants can start.';
}
